import PropTypes from 'prop-types';
import { useContext, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { AuthContext } from 'src/contexts/AuthContext';
import { piiProps } from 'src/components/DemoBlur';
import { parseApiDateTime } from 'src/utils/parseApiDateTime';

// -----------------------------------------------
// Super-admin-only view of the failures captured on a pin. The API only sends
// `errors` / `error_count` to superusers; this also checks the auth user so the
// box never renders for anyone else. All text is rendered as plain React text —
// context values are arbitrary strings.
// (Reads AuthContext directly rather than useAuth() so the pin surfaces still
// render where no AuthProvider is mounted.)

const where = (context = {}) => [context.popup, context.step].filter(Boolean).join(' › ');

const formatWhen = (value) => {
    const m = parseApiDateTime(value);
    return m ? m.format('D MMM YYYY, HH:mm') : String(value || '');
};

const reporterName = (entry) => {
    const by = entry?.reported_by_detail || {};
    return [by.first_name, by.last_name].filter(Boolean).join(' ') || 'Unknown';
};

// True when PinErrorSummary will render for this pin (superuser AND errors) —
// lets a parent skip its own wrapper instead of rendering an empty one.
export function usePinErrorsVisible(pin) {
    const isSuperuser = Boolean(useContext(AuthContext)?.user?.is_superuser);
    const count = Number(pin?.error_count) || 0;
    return isSuperuser && count > 0 && Array.isArray(pin?.errors) && pin.errors.length > 0;
}

export default function PinErrorSummary({ pin }) {
    const visible = usePinErrorsVisible(pin);
    const [detailsOpen, setDetailsOpen] = useState(false);

    const count = Number(pin?.error_count) || 0;
    const errors = Array.isArray(pin?.errors) ? pin.errors : [];
    if (!visible) return null;

    const latest = errors[0];

    return (
        <>
            <Box
                data-testid="pin-error-summary"
                onClick={(e) => e.stopPropagation()}
                sx={{
                    mt: 1,
                    p: 1,
                    borderRadius: '8px',
                    bgcolor: 'error.lighter',
                    border: '1px solid',
                    borderColor: 'error.light',
                    color: 'error.dark',
                }}
            >
                <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                    Error captured · super admin only
                </Typography>
                <Typography sx={{ fontSize: 12.5, mt: 0.25, wordBreak: 'break-word' }} {...piiProps}>
                    {latest.message}
                </Typography>
                {where(latest.context) && (
                    <Typography sx={{ fontSize: 11.5, opacity: 0.85 }}>{where(latest.context)}</Typography>
                )}
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.5 }}>
                    {count > 1 ? <Typography sx={{ fontSize: 11.5, fontWeight: 600 }}>{`${count} errors`}</Typography> : <span />}
                    <Button
                        size="small"
                        color="inherit"
                        onClick={() => setDetailsOpen(true)}
                        sx={{ textTransform: 'none', minWidth: 0, p: 0, fontSize: 12, fontWeight: 700 }}
                    >
                        Details ›
                    </Button>
                </Stack>
            </Box>
            <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Captured errors</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={1.5}>
                        {errors.map((entry, i) => {
                            const ctx = entry.context || {};
                            return (
                                <Box key={entry.id ?? i} data-testid="pin-error-entry" sx={{ pb: 1.5, borderBottom: i < errors.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }} {...piiProps}>{entry.message}</Typography>
                                    {where(ctx) && <Typography variant="caption" display="block">{where(ctx)}</Typography>}
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {`Reported by ${reporterName(entry)} · ${formatWhen(entry.created_at)}`}
                                    </Typography>
                                    {ctx.route && <Typography variant="caption" color="text.secondary" display="block">{`Route: ${ctx.route}`}</Typography>}
                                    {ctx.status !== undefined && ctx.status !== null && ctx.status !== '' && (
                                        <Typography variant="caption" color="text.secondary" display="block">{`HTTP status: ${ctx.status}`}</Typography>
                                    )}
                                </Box>
                            );
                        })}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

PinErrorSummary.propTypes = {
    pin: PropTypes.shape({
        error_count: PropTypes.number,
        errors: PropTypes.arrayOf(PropTypes.object),
    }),
};

PinErrorSummary.defaultProps = {
    pin: null,
};
